import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { Task } from "../api/types";

export function useTasks(): {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (body: unknown) => Promise<Task>;
  update: (id: string, body: unknown) => Promise<Task>;
  remove: (id: string) => Promise<void>;
  start: (id: string) => Promise<void>;
  stop: (id: string) => Promise<void>;
} {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get<{ tasks: Task[] }>("/api/tasks");
      setTasks(res.tasks);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (body: unknown) => {
      const res = await api.post<{ task: Task }>("/api/tasks", body);
      await refresh();
      return res.task;
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, body: unknown) => {
      const res = await api.put<{ task: Task }>(`/api/tasks/${id}`, body);
      await refresh();
      return res.task;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await api.delete(`/api/tasks/${id}`);
      await refresh();
    },
    [refresh],
  );

  const start = useCallback(
    async (id: string) => {
      await api.post(`/api/tasks/${id}/start`);
      await refresh();
    },
    [refresh],
  );

  const stop = useCallback(
    async (id: string) => {
      await api.post(`/api/tasks/${id}/stop`);
      await refresh();
    },
    [refresh],
  );

  return { tasks, loading, error, refresh, create, update, remove, start, stop };
}
